
import { useState, useEffect } from 'react';

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface ResponsiveState {
  isMobile: boolean;  // phones (< 768px)
  isTablet: boolean;  // tablets (≥ 768px and < 1024px)
  isLaptop: boolean;  // laptops (≥ 1024px and < 1280px)
  isDesktop: boolean; // desktop monitors (≥ 1280px)
  breakpoint: Breakpoint;
  width: number;
  height: number;
}

export function useResponsive(): ResponsiveState {
  const [state, setState] = useState<ResponsiveState>({
    isMobile: false,
    isTablet: false,
    isLaptop: false,
    isDesktop: false,
    breakpoint: 'md',
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      const newState = {
        isMobile: width < 768,  // Strict mobile breakpoint
        isTablet: width >= 768 && width < 1024,
        isLaptop: width >= 1024 && width < 1280,
        isDesktop: width >= 1280,
        breakpoint: width < 640 ? 'xs' 
                  : width < 768 ? 'sm'
                  : width < 1024 ? 'md'
                  : width < 1280 ? 'lg'
                  : 'xl' as Breakpoint,
        width,
        height
      };
      
      // Debug logging
      console.log('useResponsive Debug - Window resized to:', width, 'x', height);
      console.log('useResponsive Debug - New state:', {
        isMobile: newState.isMobile,
        isTablet: newState.isTablet,
        isLaptop: newState.isLaptop,
        isDesktop: newState.isDesktop,
        breakpoint: newState.breakpoint
      });
      
      setState(newState);
    };

    // Initial check
    handleResize();
    
    // Event listeners with debouncing
    let timeoutId: number;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(handleResize, 50);
    };
    
    window.addEventListener('resize', debouncedResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return state;
}
